import { useEffect, useState } from 'react';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import teams from './teams.json';
import { fetchGravatarProfile } from './helper/gravatar';
import TeamCard from './helper/TeamCard';

function byLastName(a, b) {
    const lastName = (p) => p.name.trim().split(' ').pop().toLowerCase();
    return lastName(a).localeCompare(lastName(b));
}

function byCurrentThenLastName(a, b) {
    if (Boolean(a.current) !== Boolean(b.current)) {
        return a.current ? -1 : 1; // current sorts ahead of former
    }
    return byLastName(a, b);
}

function TeamSection() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        async function loadTeam() {
            try {
                // Enrich every roster entry with its Gravatar data, in parallel.
                const enriched = await Promise.all(
                    teams.map(async (person) => {
                        const gravatar = await fetchGravatarProfile(
                            person.profileIdentifier
                        );
                        return { ...person, ...gravatar };
                    })
                );
                if (!cancelled) setMembers(enriched);
            } catch (error) {
                console.error('Failed to load team:', error);
                if (!cancelled) setMembers(teams); // fall back to raw roster
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        loadTeam();
        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) {
        return (
            <section id="team" className="py-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading team…</span>
                </Spinner>
            </section>
        );
    }

    const leaders = members.filter((m) => m.leader).sort(byCurrentThenLastName);
    const contributors = members
        .filter((m) => !m.leader)
        .sort(byCurrentThenLastName);

    const renderGroup = (group) => (
        <Row className="g-4 justify-content-center">
            {group.map((person) => (
                <Col key={person.id} xs={12} sm={6} md={4} lg={3}>
                    <TeamCard
                        name={person.name}
                        title={person.title}
                        university={person.university}
                        timeline={person.timeline}
                        avatarUrl={person.avatar_url}
                        profileUrl={person.profile_url}
                    />
                </Col>
            ))}
        </Row>
    );

    return (
        <section id="team" className="py-5">
            <Container>
                <h2 className="text-center mb-4">Meet the Team</h2>

                {leaders.length > 0 && (
                    <>
                        <h3 className="h4 mb-3">Leaders</h3>
                        {renderGroup(leaders)}
                    </>
                )}

                {contributors.length > 0 && (
                    <>
                        <h3 className="h4 mt-5 mb-3">Student Contributors</h3>
                        {renderGroup(contributors)}
                    </>
                )}
            </Container>
        </section>
    );
}

export default TeamSection;
